import React, { useState, useEffect, useRef } from 'react';
import { PencilIcon, CheckIcon, XIcon, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { validateFieldSimple } from '@/schemas/product';

interface EnhancedInlineEditFieldProps {
  label: string;
  fieldName: string;
  value: string | null;
  placeholder?: string;
  onSave: (newValue: string) => Promise<void>;
  validate?: (value: string) => string | true;
  disabled?: boolean;
  className?: string;
  context?: string;
  enhanced?: boolean;
  organizationId?: string;
  realTimeValidation?: boolean;
}

/**
 * Enhanced inline edit field that uses the unified validation system
 * while maintaining backward compatibility with custom validation
 */
export function EnhancedInlineEditField({
  label,
  fieldName,
  value,
  placeholder,
  onSave,
  validate,
  disabled = false,
  className,
  context: _context = 'update',
  enhanced: _enhanced = true,
  organizationId: _organizationId,
  realTimeValidation: _realTimeValidation = false,
}: EnhancedInlineEditFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [isSaving, setIsSaving] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update local state when value prop changes
  useEffect(() => {
    setEditValue(value || '');
  }, [value]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleEdit = () => {
    if (disabled) return;
    setIsEditing(true);
    setValidationError(null);
  };

  const handleCancel = () => {
    setEditValue(value || '');
    setIsEditing(false);
    setValidationError(null);
  };

  const handleSave = async () => {
    // Custom validation takes precedence (for translation support)
    if (validate) {
      const customValidationResult = validate(editValue);
      if (customValidationResult !== true) {
        setValidationError(customValidationResult);
        return;
      }
    } else {
      // Fallback to simple enhanced validation only if no custom validation provided
      const validationResult = validateFieldSimple(fieldName, editValue);

      if (!validationResult.isValid) {
        setValidationError(validationResult.error || 'Validation failed');
        return;
      }
    }

    // Don't save if value hasn't changed
    if (editValue === (value || '')) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    setValidationError(null);

    try {
      await onSave(editValue);
      setIsEditing(false);
      toast.success(`${label} updated successfully`);
    } catch (error: any) {
      // Handle server-side validation errors
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        `Failed to update ${label.toLowerCase()}`;

      setValidationError(errorMessage);
      toast.error(`Failed to update ${label.toLowerCase()}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  const handleClickOutside = (e: React.FocusEvent) => {
    // Only cancel if we're not clicking on the save/cancel buttons
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (relatedTarget?.closest('[data-inline-edit-controls]')) {
      return;
    }
    handleCancel();
  };

  const hasValidationError = validationError !== null;

  if (isEditing) {
    return (
      <div className={cn('space-y-2', className)}>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Input
              ref={inputRef}
              value={editValue}
              onChange={e => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleClickOutside}
              placeholder={placeholder}
              disabled={isSaving}
              className={cn(
                'h-8 text-sm pr-8',
                hasValidationError && 'border-red-500 focus-visible:ring-red-500'
              )}
              aria-label={`Edit ${label}`}
              aria-invalid={hasValidationError}
            />
          </div>
          <div className="flex items-center gap-1" data-inline-edit-controls>
            <Button
              size="sm"
              variant="ghost"
              className="size-8 p-0"
              onClick={handleSave}
              disabled={isSaving || hasValidationError}
              aria-label={`Save ${label}`}
            >
              {isSaving ? (
                <Loader2 className="size-3 animate-spin" />
              ) : (
                <CheckIcon
                  className={cn('h-3 w-3', hasValidationError ? 'text-gray-400' : 'text-green-600')}
                />
              )}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="size-8 p-0"
              onClick={handleCancel}
              disabled={isSaving}
              aria-label={`Cancel editing ${label}`}
            >
              <XIcon className="size-3 text-gray-500" />
            </Button>
          </div>
        </div>
        {hasValidationError && (
          <p className="text-xs text-red-500" role="alert">
            {validationError}
          </p>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex justify-between items-center bg-muted/30 dark:bg-muted/20 rounded-md px-3 py-1.5 border border-border group hover:bg-muted/50 dark:hover:bg-muted/30 transition-colors',
        className
      )}
    >
      <span className="text-sm text-muted-foreground">{label}</span>
      <div className="flex items-center gap-2">
        <span
          className="max-w-[200px] truncate text-right text-sm font-medium text-foreground"
          title={value || 'Not specified'}
        >
          {value || 'Not specified'}
        </span>
        {!disabled && (
          <Button
            size="sm"
            variant="ghost"
            className="size-6 p-0 text-muted-foreground transition-all duration-200 hover:scale-110 hover:bg-accent hover:text-foreground"
            onClick={handleEdit}
            aria-label={`Edit ${label}`}
          >
            <PencilIcon className="size-3" />
          </Button>
        )}
      </div>
    </div>
  );
}

export default EnhancedInlineEditField;
